import React, { useState, useEffect } from 'react';
import { usePresence, UserStatus } from '../../context/PresenceContext';
import { StatusSelector } from './StatusIndicator';
import { X, Bell, Clock, MessageSquare, Building } from 'lucide-react';

const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
  { value: 'UTC', label: 'UTC' }
];

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' }
];

const PresenceSettingsModal = ({ isOpen, onClose }) => {
  const { myPresence, updateStatus, updateSettings } = usePresence();
  const [settings, setSettings] = useState({
    custom_status_text: '',
    auto_away_enabled: true,
    auto_away_minutes: 15,
    auto_response_enabled: false,
    auto_response_busy: '',
    auto_response_away: '',
    auto_response_dnd: '',
    notification_sound_enabled: true,
    desktop_notifications_enabled: true,
    show_message_preview: true,
    // Office hours settings
    office_hours_enabled: false,
    office_hours_start: '09:00',
    office_hours_end: '17:00',
    office_hours_timezone: 'America/New_York',
    office_hours_days: '1,2,3,4,5',
    office_hours_away_message: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (myPresence) {
      setSettings({
        custom_status_text: myPresence.custom_status_text || '',
        auto_away_enabled: myPresence.auto_away_enabled ?? true,
        auto_away_minutes: myPresence.auto_away_minutes ?? 15,
        auto_response_enabled: myPresence.auto_response_enabled ?? false,
        auto_response_busy: myPresence.auto_response_busy || '',
        auto_response_away: myPresence.auto_response_away || '',
        auto_response_dnd: myPresence.auto_response_dnd || '',
        notification_sound_enabled: myPresence.notification_sound_enabled ?? true,
        desktop_notifications_enabled: myPresence.desktop_notifications_enabled ?? true,
        show_message_preview: myPresence.show_message_preview ?? true,
        // Office hours settings
        office_hours_enabled: myPresence.office_hours_enabled ?? false,
        office_hours_start: myPresence.office_hours_start || '09:00',
        office_hours_end: myPresence.office_hours_end || '17:00',
        office_hours_timezone: myPresence.office_hours_timezone || 'America/New_York',
        office_hours_days: myPresence.office_hours_days || '1,2,3,4,5',
        office_hours_away_message: myPresence.office_hours_away_message || ''
      });
    }
  }, [myPresence]);

  // Helper to toggle a day in the office_hours_days string
  const toggleDay = (dayValue) => {
    const currentDays = settings.office_hours_days.split(',').filter(d => d).map(Number);
    let newDays;
    if (currentDays.includes(dayValue)) {
      newDays = currentDays.filter(d => d !== dayValue);
    } else {
      newDays = [...currentDays, dayValue].sort((a, b) => a - b);
    }
    setSettings({ ...settings, office_hours_days: newDays.join(',') });
  };

  const selectedDays = settings.office_hours_days.split(',').filter(d => d).map(Number);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await updateSettings(settings);
      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 500);
    } catch (err) {
      console.error('Failed to save settings:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-800 border border-cyan-500/30 rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-cyan-500/20 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">Presence Settings</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-slate-700 rounded-lg transition text-slate-400 hover:text-white"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {/* Current Status */}
          <div>
            <label className="text-sm font-medium text-slate-300 mb-2 block">Current Status</label>
            <StatusSelector
              currentStatus={myPresence?.status || UserStatus.AVAILABLE}
              onSelect={(status) => updateStatus(status)}
            />
          </div>

          {/* Custom Status Text */}
          <div>
            <label className="text-sm font-medium text-slate-300 mb-2 block">Status Message</label>
            <input
              type="text"
              value={settings.custom_status_text}
              onChange={(e) => setSettings({ ...settings, custom_status_text: e.target.value })}
              placeholder="What are you working on?"
              maxLength={100}
              className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:border-cyan-500 focus:outline-none"
            />
          </div>

          {/* Auto-Away Settings */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock size={18} className="text-cyan-400" />
                <span className="text-sm font-medium text-slate-300">Auto-Away</span>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.auto_away_enabled}
                  onChange={(e) => setSettings({ ...settings, auto_away_enabled: e.target.checked })}
                  className="sr-only"
                />
                <div className={`w-10 h-5 rounded-full transition ${settings.auto_away_enabled ? 'bg-cyan-500' : 'bg-slate-600'}`}>
                  <div className={`w-4 h-4 rounded-full bg-white shadow transform transition mt-0.5 ${settings.auto_away_enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </div>
              </label>
            </div>
            {settings.auto_away_enabled && (
              <div className="ml-6">
                <label className="text-xs text-slate-400">Mark away after inactivity</label>
                <select
                  value={settings.auto_away_minutes}
                  onChange={(e) => setSettings({ ...settings, auto_away_minutes: parseInt(e.target.value) })}
                  className="mt-1 w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:border-cyan-500 focus:outline-none text-sm"
                >
                  <option value="5">5 minutes</option>
                  <option value="10">10 minutes</option>
                  <option value="15">15 minutes</option>
                  <option value="30">30 minutes</option>
                  <option value="60">1 hour</option>
                </select>
              </div>
            )}
          </div>

          {/* Auto-Response Settings */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MessageSquare size={18} className="text-cyan-400" />
                <span className="text-sm font-medium text-slate-300">Auto-Responses</span>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.auto_response_enabled}
                  onChange={(e) => setSettings({ ...settings, auto_response_enabled: e.target.checked })}
                  className="sr-only"
                />
                <div className={`w-10 h-5 rounded-full transition ${settings.auto_response_enabled ? 'bg-cyan-500' : 'bg-slate-600'}`}>
                  <div className={`w-4 h-4 rounded-full bg-white shadow transform transition mt-0.5 ${settings.auto_response_enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </div>
              </label>
            </div>

            {settings.auto_response_enabled && (
              <div className="ml-6 space-y-3">
                <div>
                  <label className="text-xs text-slate-400">When Busy</label>
                  <input
                    type="text"
                    value={settings.auto_response_busy}
                    onChange={(e) => setSettings({ ...settings, auto_response_busy: e.target.value })}
                    placeholder="I'm currently busy and will respond soon."
                    className="mt-1 w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:border-cyan-500 focus:outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-400">When Away</label>
                  <input
                    type="text"
                    value={settings.auto_response_away}
                    onChange={(e) => setSettings({ ...settings, auto_response_away: e.target.value })}
                    placeholder="I'm away from my desk right now."
                    className="mt-1 w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:border-cyan-500 focus:outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-400">When Do Not Disturb</label>
                  <input
                    type="text"
                    value={settings.auto_response_dnd}
                    onChange={(e) => setSettings({ ...settings, auto_response_dnd: e.target.value })}
                    placeholder="Please don't disturb. I'll check messages later."
                    className="mt-1 w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:border-cyan-500 focus:outline-none text-sm"
                  />
                </div>
                <p className="text-xs text-slate-500">
                  Auto-responses are sent once per sender every 30 minutes.
                </p>
              </div>
            )}
          </div>

          {/* Office Hours Settings */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Building size={18} className="text-cyan-400" />
                <span className="text-sm font-medium text-slate-300">Office Hours</span>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.office_hours_enabled}
                  onChange={(e) => setSettings({ ...settings, office_hours_enabled: e.target.checked })}
                  className="sr-only"
                />
                <div className={`w-10 h-5 rounded-full transition ${settings.office_hours_enabled ? 'bg-cyan-500' : 'bg-slate-600'}`}>
                  <div className={`w-4 h-4 rounded-full bg-white shadow transform transition mt-0.5 ${settings.office_hours_enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </div>
              </label>
            </div>
            <p className="text-xs text-slate-500 ml-6">
              Automatically show as away outside your office hours
            </p>

            {settings.office_hours_enabled && (
              <div className="ml-6 space-y-3">
                {/* Days of week */}
                <div>
                  <label className="text-xs text-slate-400 block mb-2">Working Days</label>
                  <div className="flex gap-1">
                    {DAYS_OF_WEEK.map((day) => (
                      <button
                        key={day.value}
                        type="button"
                        onClick={() => toggleDay(day.value)}
                        className={`px-2 py-1 text-xs rounded transition ${
                          selectedDays.includes(day.value)
                            ? 'bg-cyan-500 text-white'
                            : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                        }`}
                      >
                        {day.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Start and End Time */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-400">Start Time</label>
                    <input
                      type="time"
                      value={settings.office_hours_start}
                      onChange={(e) => setSettings({ ...settings, office_hours_start: e.target.value })}
                      className="mt-1 w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:border-cyan-500 focus:outline-none text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400">End Time</label>
                    <input
                      type="time"
                      value={settings.office_hours_end}
                      onChange={(e) => setSettings({ ...settings, office_hours_end: e.target.value })}
                      className="mt-1 w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:border-cyan-500 focus:outline-none text-sm"
                    />
                  </div>
                </div>

                {/* Timezone */}
                <div>
                  <label className="text-xs text-slate-400">Timezone</label>
                  <select
                    value={settings.office_hours_timezone}
                    onChange={(e) => setSettings({ ...settings, office_hours_timezone: e.target.value })}
                    className="mt-1 w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:border-cyan-500 focus:outline-none text-sm"
                  >
                    {TIMEZONES.map((tz) => (
                      <option key={tz.value} value={tz.value}>{tz.label}</option>
                    ))}
                  </select>
                </div>

                {/* Away Message */}
                <div>
                  <label className="text-xs text-slate-400">Outside Office Hours Message</label>
                  <textarea
                    value={settings.office_hours_away_message}
                    onChange={(e) => setSettings({ ...settings, office_hours_away_message: e.target.value })}
                    placeholder="I'm currently outside office hours. I'll respond during business hours."
                    rows={2}
                    className="mt-1 w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:border-cyan-500 focus:outline-none text-sm resize-none"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Notification Settings */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-slate-300 flex items-center gap-2">
              <Bell size={18} className="text-cyan-400" />
              Notifications
            </h3>

            <label className="flex items-center justify-between cursor-pointer py-2">
              <span className="text-sm text-slate-400">Notification sound</span>
              <input
                type="checkbox"
                checked={settings.notification_sound_enabled}
                onChange={(e) => setSettings({ ...settings, notification_sound_enabled: e.target.checked })}
                className="sr-only"
              />
              <div className={`w-10 h-5 rounded-full transition ${settings.notification_sound_enabled ? 'bg-cyan-500' : 'bg-slate-600'}`}>
                <div className={`w-4 h-4 rounded-full bg-white shadow transform transition mt-0.5 ${settings.notification_sound_enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </div>
            </label>

            <label className="flex items-center justify-between cursor-pointer py-2">
              <span className="text-sm text-slate-400">Desktop notifications</span>
              <input
                type="checkbox"
                checked={settings.desktop_notifications_enabled}
                onChange={(e) => setSettings({ ...settings, desktop_notifications_enabled: e.target.checked })}
                className="sr-only"
              />
              <div className={`w-10 h-5 rounded-full transition ${settings.desktop_notifications_enabled ? 'bg-cyan-500' : 'bg-slate-600'}`}>
                <div className={`w-4 h-4 rounded-full bg-white shadow transform transition mt-0.5 ${settings.desktop_notifications_enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </div>
            </label>

            <label className="flex items-center justify-between cursor-pointer py-2">
              <span className="text-sm text-slate-400">Show message preview</span>
              <input
                type="checkbox"
                checked={settings.show_message_preview}
                onChange={(e) => setSettings({ ...settings, show_message_preview: e.target.checked })}
                className="sr-only"
              />
              <div className={`w-10 h-5 rounded-full transition ${settings.show_message_preview ? 'bg-cyan-500' : 'bg-slate-600'}`}>
                <div className={`w-4 h-4 rounded-full bg-white shadow transform transition mt-0.5 ${settings.show_message_preview ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </div>
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-cyan-500/20 space-y-3">
          {error && (
            <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}
          {success && (
            <div className="p-3 bg-green-500/20 border border-green-500/30 rounded-lg text-green-400 text-sm">
              Settings saved successfully!
            </div>
          )}
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-slate-400 hover:text-white transition"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white rounded-lg font-medium transition disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PresenceSettingsModal;
